import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { track } from "@/lib/site";

export function Reveal({ as: Tag = "div", delay = 0, className = "", children, ...rest }) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return; }
        const io = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) { setInView(true); io.disconnect(); }
        }, { rootMargin: "0px 0px -8% 0px" });
        io.observe(el);
        return () => io.disconnect();
    }, []);
    return (
        <Tag ref={ref} className={`reveal ${inView ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>
            {children}
        </Tag>
    );
}

export function Eyebrow({ children, dark = false, className = "" }) {
    return (
        <p className={`mono-label flex items-center gap-3 ${dark ? "text-sage" : "text-moss"} ${className}`}>
            <span className={`h-px w-8 ${dark ? "bg-sage/60" : "bg-moss/50"}`} />
            {children}
        </p>
    );
}

export function ArrowLink({ to, children, dark = false, testid, className = "" }) {
    return (
        <Link to={to} data-testid={testid}
              className={`group inline-flex items-center gap-3 mono-label transition-colors ${dark ? "text-cream hover:text-sage" : "text-forest hover:text-moss"} ${className}`}>
            <span className="border-b border-current pb-1">{children}</span>
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
    );
}

export function OutLink({ href, event, label, children, className = "", testid, icon = true }) {
    if (!href) return null;
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
           onClick={() => event && track(event, label || href)}
           className={`group inline-flex items-center gap-2 ${className}`}>
            {children}
            {icon && <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
        </a>
    );
}

export function PillButton({ to, children, variant = "dark", testid, className = "", onClick }) {
    const styles = {
        dark: "bg-forest text-cream hover:bg-forest-hover",
        light: "bg-cream text-forest hover:bg-sage",
        ghost: "border border-cream/50 text-cream hover:bg-cream hover:text-forest",
        outline: "border border-forest/40 text-forest hover:bg-forest hover:text-cream",
    };
    return (
        <Link to={to} data-testid={testid} onClick={onClick}
              className={`group inline-flex items-center justify-center gap-3 rounded-full px-7 py-4 text-sm font-medium tracking-wide transition-colors duration-300 ${styles[variant]} ${className}`}>
            {children}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
    );
}

export function Tbc({ children = "To be confirmed", className = "" }) {
    return (
        <span data-testid="tbc-placeholder"
              className={`inline-flex items-center rounded-full border border-dashed border-moss/60 px-3 py-1 text-xs font-mono uppercase tracking-[0.06em] text-moss ${className}`}>
            {children}
        </span>
    );
}

export function StatusBadge({ status, dark = false }) {
    return (
        <span data-testid="project-status"
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.08em] ${dark ? "bg-cream/15 text-cream backdrop-blur-md" : "bg-forest text-cream"}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-sage animate-pulse" />
            {status}
        </span>
    );
}

export function PageHero({ eyebrow, title, intro, image, imageAlt, testid }) {
    return (
        <section data-testid={testid} className="relative isolate overflow-hidden bg-forest text-cream pt-36 md:pt-44 pb-20 md:pb-28">
            {image && (
                <>
                    <img src={image} alt={imageAlt} className="absolute inset-0 -z-10 h-full w-full object-cover hero-zoom" loading="eager" />
                    <div className="absolute inset-0 -z-10 bg-gradient-to-b from-forest/70 via-forest/60 to-forest/95" />
                </>
            )}
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Reveal><Eyebrow dark>{eyebrow}</Eyebrow></Reveal>
                <Reveal delay={120}>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight max-w-4xl mt-8">{title}</h1>
                </Reveal>
                {intro && (
                    <Reveal delay={240}>
                        <p className="mt-8 max-w-2xl text-base md:text-lg text-cream/80 leading-relaxed">{intro}</p>
                    </Reveal>
                )}
            </div>
        </section>
    );
}
